/* eslint-disable no-console */
import { useCallback } from 'react';
import {
  EntityChangeType,
  ProjectTypeEnum,
} from '../../../../../server/graphql/__generated.types';
import { ProjectFragment } from '../../../fragments.generated';
import useIsOutOfOrderEvent from '../../../../util/subscriptionUtils';
import { useProjectEntityChangedSubscription } from '../../operation.generated';
import useProjectCacheUtilities from '../../useProjectCacheUtilities';
import { usePromptsContext } from '../../../../context/prompts/PromptsContext';
import { useAppDispatch } from '../../../../store/hooks';
import { setSelectedProjects } from '../../../../store/DrivesSlice';

const useProjectEntityChanged = (
  projectId: string | null,
  type: ProjectTypeEnum | null
) => {
  const [, dispatchPrompt] = usePromptsContext();
  const appDispatch = useAppDispatch();

  const handleProjectDeletion = useCallback(() => {
    dispatchPrompt({
      type: 'OpenEntityChangedNotificationPrompt',
      payload: {
        header: 'Permissions Error',
        body: 'Your project was deleted by the project owner.',
        linkUrl: '/dashboard/projects/personal',
      },
    });
  }, [dispatchPrompt]);

  const { updateProjectInCache, removeProjectFromCache, readProjectFragment } =
    useProjectCacheUtilities();

  const isOutOfOrderEvent = useIsOutOfOrderEvent();

  return useProjectEntityChangedSubscription({
    variables: {
      inputs: {
        projectId,
        type,
      },
    },
    onData: data => {
      const projectEvent = data.data?.data?.projectEntityChanged;
      if (!projectEvent) {
        console.error('projectEvent not found');
        return;
      }
      const { projectEntity, ...event } = projectEvent;
      const changeType = event?.changeType;

      if (!projectEntity) {
        console.error('projectEntity not found');
        return;
      }

      // check the timing of events
      const outOfOrderEvent = isOutOfOrderEvent(
        projectEntity.id,
        projectEvent.eventTime
      );
      if (outOfOrderEvent) {
        console.info(
          'out of order event occurred within useProjectEntityChanged'
        );
        return;
      }

      // we need the existingProject in the cache for clipsCount, sequence, and projectAccess
      const existingProject = readProjectFragment(projectEntity.id);
      if (!existingProject) {
        console.error('existingProject not found in cache');
        return;
      }

      // cast the SubscriptionProject as a ProjectFragment
      const incomingProject: ProjectFragment = {
        ...projectEntity,
        __typename: 'Project',
        clipsCount: existingProject.clipsCount,
        sequence: existingProject.sequence,
        projectAccess: existingProject.projectAccess,
        studioVersion: existingProject.studioVersion,
        sampleRate: existingProject.sampleRate,
      };

      switch (changeType) {
        case EntityChangeType.Updated: {
          updateProjectInCache(incomingProject);
          break;
        }
        case EntityChangeType.Deleted: {
          removeProjectFromCache(incomingProject);
          appDispatch(
            setSelectedProjects({
              arr: [{ id: incomingProject.id }],
              selected: false,
            })
          );
          if (projectId) {
            // only open prompt if within [projectId]
            handleProjectDeletion();
          }
          break;
        }
        default:
          // this default should not be hit
          console.error(
            `error: something went wrong in useProjectEntityChangedSubscription`
          );
      }
    },
  });
};

export default useProjectEntityChanged;
